<template>
  <div class="adv-my-container">
    <!-- 空状态 -->
    <div class="empty-state" v-if="videoList.length === 0 && !loading">
      <div class="empty-icon">📭</div>
      <h3>{{ $t('emptyData') }}</h3>
      <p class="empty-desc">{{ $t('emptyDataDesc') }}</p>
    </div>

    <!-- 列表 -->
    <div v-else class="adv-my-list">
      <div class="adv-my-card" v-for="video in videoList" :key="video.id">
        <!-- ===== 上半部分 ===== -->
        <div class="adv-card-top">
          <!-- 封面 -->
          <div class="adv-my-cover">
            <img :src="video.img" :alt="video.title" class="cover-img" />
            <div class="cover-status" v-if="video.status === 3 && video.sy_data == null">
              <span class="status-badge badge-active">{{ $t('preheat') }}</span>
            </div>
            <div class="cover-status" v-else-if="video.status === 3">
              <span class="status-badge badge-active">{{ $t('progress') }}</span>
            </div>
            <div class="cover-status" v-else-if="video.status === 4">
              <span class="status-badge badge-settlement">{{ $t('settlement') }}</span>
            </div>
            <div class="cover-status" v-else-if="video.status === 2">
              <span class="status-badge badge-success">{{ $t('completed') }}</span>
            </div>
          </div>

          <!-- 信息 -->
          <div class="adv-my-info">
            <div class="info-header">
              <h4 class="adv-title">{{ video.title }}</h4>
              <span class="adv-code">{{ $t('activityCode') }}: {{ video.code }}</span>
            </div>

            <div class="adv-stats">
              <div class="stat-item">
                <span class="stat-label">{{ $t('touzAmount') }}</span>
                <span class="stat-value stat-amount">${{ video.amount }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('activityDays') }}</span>
                <span class="stat-value">{{ video.days || 0 }} {{ $t('day') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('joinTime') }}</span>
                <span class="stat-value stat-time">{{ formatDate(video.create_time) }}</span>
              </div>
            </div>

            <!-- <div class="adv-footer">
              <div style="flex: 1"></div>
              <button v-if="video.status === 1" class="btn-pay" @click="onPay(video)">立即划转</button>
            </div> -->

            <!-- 播放来源 -->
            <div class="adv-source-right">
              <div class="source-header">
                <span class="source-title">{{ $t('playSource') }}</span>
                <span class="source-platform">{{ $t('allPlatforms') }}</span>
              </div>
              <div class="source-list">
                <div class="source-item">
                  <div class="source-info">
                    <span class="source-name"><span class="source-dot dot-facebook"></span>Facebook</span>
                    <span class="source-percent">{{ (video?.video_play?.facebook * 100 || 0).toFixed(2) }}%</span>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: video?.video_play?.facebook * 100 + '%', background: '#1877f2' }"></div>
                  </div>
                  <span class="source-count">{{ video?.video_play?.facebook_num || 0 }}</span>
                </div>
                <div class="source-item">
                  <div class="source-info">
                    <span class="source-name"><span class="source-dot dot-instagram"></span>Instagram</span>
                    <span class="source-percent">{{ (video?.video_play?.instagram * 100 || 0).toFixed(2) }}%</span>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: video?.video_play?.instagram * 100 + '%', background: '#e4405f' }"></div>
                  </div>
                  <span class="source-count">{{ (video?.video_play?.instagram_num || 0).toFixed(2) }}</span>
                </div>
                <div class="source-item">
                  <div class="source-info">
                    <span class="source-name"><span class="source-dot dot-tiktok"></span>TikTok</span>
                    <span class="source-percent">{{ (video?.video_play?.tiktok * 100 || 0).toFixed(2) }}%</span>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: video?.video_play?.tiktok * 100 + '%', background: '#F44336' }"></div>
                  </div>
                  <span class="source-count">{{ (video?.video_play?.tiktok_num || 0).toFixed(2) }}</span>
                </div>
                <div class="source-item">
                  <div class="source-info">
                    <span class="source-name"><span class="source-dot dot-youtube"></span>YouTube</span>
                    <span class="source-percent">{{ (video?.video_play?.youtube * 100 || 0).toFixed(2) }}%</span>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: video?.video_play?.youtube * 100 + '%', background: '#4285f4' }"></div>
                  </div>
                  <span class="source-count">{{ (video?.video_play?.youtube_num || 0).toFixed(2) }}</span>
                </div>
                <div class="source-item">
                  <div class="source-info">
                    <span class="source-name"><span class="source-dot dot-other"></span>{{ $t('other') }}</span>
                    <span class="source-percent">{{ (video?.video_play?.other * 100 || 0).toFixed(2) }}%</span>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: video?.video_play?.other * 100 + '%', background: '#6c757d' }"></div>
                  </div>
                  <span class="source-count">{{ video?.video_play?.other_num || 0 }}</span>
                </div>
              </div>
              <!-- <div class="source-total">
                <span class="total-label">{{ $t('totalPlayNum') }}</span>
                <span class="total-value">{{ video?.video_play?.total_play_num || 0 }}</span>
              </div> -->
            </div>
          </div>
        </div>

        <!-- ===== 下半部分 ===== -->
        <div class="adv-card-bottom">
          <div class="period-section">
            <div class="period-header">
              <span class="period-title">{{ $t('incomeSummary') }}</span>
              <span class="period-desc"></span>
            </div>
            <!-- 今日支付汇总 -->
            <div class="summary-box">
              <div class="summary-grid">
                <div class="summary-item">
                  <span class="summary-label">{{ $t('totalIncome') }}</span>
                  <span class="summary-value income-orange">${{ video?.totals?.total_income || 0 }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">{{ $t('totalPay') }}</span>
                  <span class="summary-value">${{ video?.totals?.total_pay || 0 }}</span>
                </div>

                <div class="summary-item">
                  <span class="summary-label">{{ $t('buyNum') }}</span>
                  <span class="summary-value">{{ video?.totals?.total_buy_num || 0 }}</span>
                </div>

                <div class="summary-item">
                  <span class="summary-label">{{ $t('totalPlayNum') }}</span>
                  <span class="summary-value">{{ video?.totals?.total_play_num || 0 }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">{{ $t('copyrightRatio') }}</span>
                  <span class="summary-value">{{ video?.totals?.copyright_ratio ? video?.totals?.copyright_ratio * 100 : 0 }}%</span>
                </div>
              </div>
            </div>

            <div class="period-header" v-if="video.sy_data">
              <span class="period-title">{{ $t('periodPayment') }}</span>
              <div class="summary-title">
                <span style="font-weight: bold; font-size: 16px; color: #ff9800" v-if="video?.sy_date">[{{ video?.sy_date }}]</span>
              </div>
              <span class="period-desc">{{ $t('periodPaymentDesc') }}</span>
            </div>

            <div class="period-grid">
              <div class="period-card" v-for="(item, i) in video.sy_data" :key="i">
                <div class="period-time">
                  <span class="period-label" v-if="item.status == 1 && item.sort > item.now_sort">{{ $t('preheat') }}</span>
                  <span class="period-label period-jxz" v-else-if="item.status == 1">{{ $t('progress') }}</span>
                  <span class="period-label" v-else-if="item.status == 2 && item.store_income == 0">{{ $t('completedtlement') }}</span>
                  <span class="period-label" v-else-if="item.status == 2">{{ $t('transfered') }}</span>
                  <span class="period-label" v-if="item.status == 3">{{ $t('settlement') }}</span>
                  <span class="period-range">{{ item.time_period.begin }} - {{ item.time_period.end }}</span>
                  <span class="period-name">{{ timePeriod[i].title }}</span>
                </div>
                <div class="period-details">
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('promotionIncome') }}</span>
                    <span class="period-row-value">${{ item.income }}</span>
                  </div>
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('copyrightIncome') }}</span>
                    <span class="period-row-value">${{ item.store_income }}</span>
                  </div>
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('playNum') }}</span>
                    <span class="period-row-value">{{ item.play_num }}</span>
                  </div>
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('buyNum') }}</span>
                    <span class="period-row-value">{{ item.buy_num }}</span>
                  </div>
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('incomeRate') }}</span>
                    <span class="period-row-value">{{ item.rate * 100 }}%</span>
                  </div>
                  <div class="period-row">
                    <span class="period-row-label">{{ $t('yjIncome') }}</span>
                    <span class="period-row-value income-orange">${{ item.real_income }}</span>
                  </div>
                </div>
                <!-- 状态 -->
                <template v-if="item.status == 1 && item.sort > item.now_sort">
                  <div class="period-pay-btn3"></div>
                  <div class="period-status status-transferred">
                    <span class="status-icon"></span>
                    <!-- <span class="period-yrz">预热中</span> -->
                  </div>
                </template>
                <template v-else-if="item.status == 1">
                  <div class="period-pay-btn3"></div>
                  <div class="period-status status-transferred">
                    <span class="status-icon"></span>
                    <!-- <span class="period-jxz">进行中</span> -->
                  </div>
                </template>
                <template v-else-if="item.status == 2 && item.store_income == 0">
                  <div class="period-pay-btn3"></div>
                  <div class="period-status status-transferred">
                    <span class="status-icon"></span>
                    <span class="status-text">-</span>
                  </div>
                </template>
                <template v-else-if="item.status == 2">
                  <div class="period-pay-btn2">{{ $t('paid') }}: ${{ item.store_income }}</div>
                  <div class="period-status status-transferred">
                    <!-- <span class="status-icon">✅</span> -->
                    <!-- <span class="status-text">已划转</span> -->
                  </div>
                </template>
                <template v-else-if="item.status == 3 && item.expired_seconds == 0">
                  <button class="period-pay-btn expired" @click="onTransfer(video, item)">{{ $t('transfer') }}</button>
                  <div class="period-status status-expired">
                    <span class="status-icon">⛔</span>
                    <span class="status-text">{{ $t('timeout') }}</span>
                  </div>
                </template>
                <template v-else-if="item.status == 3 && item.expired_seconds > 0">
                  <button class="period-pay-btn" @click="onTransfer(video, item)">{{ $t('transfer') }}</button>
                  <div class="period-countdown">
                    <span class="countdown-icon">⏳</span>
                    <span class="countdown-time">{{ getCountdown(video, i) }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载更多 -->
    <div class="load-more">
      <button class="load-btn" @click="loadMore" v-if="hasMore" :disabled="loading">
        <span class="loader" v-if="loading"></span>
        {{ loading ? $t('loading') : $t('loadMore') }}
      </button>
      <div class="no-more" v-else>{{ $t('noMore') }}</div>
    </div>

    <AdvTransfer ref="transfer" @success="onTransferSuccess"></AdvTransfer>
  </div>
</template>

<script>
import AdvTransfer from '@/components/adv/AdvTransfer.vue'

export default {
  components: { AdvTransfer },
  props: {
    searchKeyword: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      hasMore: true,
      page: 1,
      size: 4,
      videoList: [],
      countdownText: {},
      countdownTimer: null,
      timePeriod: [
        {
          title: this.$t('nightTime'),
          begin: '00:00',
          end: '06:00'
        },
        {
          title: this.$t('morningTime'),
          begin: '06:00',
          end: '12:00'
        },
        {
          title: this.$t('afternoonTime'),
          begin: '12:00',
          end: '18:00'
        },
        {
          title: this.$t('eveningTime'),
          begin: '18:00',
          end: '24:00'
        }
      ],
      // 搜索相关
      searchTimer: null, // 防抖定时器
      abortController: null, // 请求取消控制器
      currentKeyword: '' // 当前搜索的关键词
    }
  },
  created() {
    this.getList()
  },
  beforeUnmount() {
    this.stopCountdown()

    // 清理定时器和取消请求
    clearTimeout(this.searchTimer)
    this.cancelRequest()
  },
  // 2. 监听 searchKeyword 变化
  watch: {
    searchKeyword: {
      handler(newVal) {
        // 清除之前的防抖定时器
        clearTimeout(this.searchTimer)

        // 如果关键词为空或长度小于2，重置列表
        if (!newVal || newVal.trim().length < 1) {
          this.currentKeyword = ''
          this.getList(1)
          return
        }

        // 设置防抖，300ms 后执行搜索
        this.searchTimer = setTimeout(() => {
          this.currentKeyword = newVal.trim()
          this.getList(1, this.currentKeyword)
        }, 300)
      },
      immediate: true
    }
  },
  methods: {
    // 取消当前请求
    cancelRequest() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    },

    // 重置列表
    resetList() {
      // this.videoList = []
      this.page = 1
      this.hasMore = true
      this.loading = false
      this.cancelRequest()
    },
    updateAllCountdowns() {
      this.videoList.forEach((video) => {
        //没有日收益数据，跳过
        if (!video.sy_data) return
        video.sy_data.forEach((item, index) => {
          if (item.status == 2 || item.expired_seconds <= 0) return
          item.expired_seconds--
          const key = `${video.id}_${index}`
          this.countdownText[key] = this.formatCountdown(item.expired_seconds)
        })
      })
    },

    startCountdown() {
      if (this.countdownTimer) return
      this.updateAllCountdowns()
      this.countdownTimer = setInterval(() => {
        this.updateAllCountdowns()
      }, 1000)
    },

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    clearCountdownData() {
      this.countdownText = {}
      this.stopCountdown()
    },

    getCountdown(video, index) {
      const key = `${video.id}_${index}`
      return this.countdownText[key] || '--:--:--'
    },

    formatCountdown(seconds) {
      if (seconds < 0) return '--:--:--'
      const h = String(Math.floor(seconds / 3600)).padStart(2, '0')
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
      const s = String(seconds % 60).padStart(2, '0')
      return `${h}:${m}:${s}`
    },

    onPay(item) {
      this.$router.push({
        name: 'adv_buy',
        query: { code: item.code }
      })
    },
    onSuccess() {
      this.getList(1)
    },
    onTransfer(video, item) {
      this.$refs.transfer.open(video, item)
    },
    onTransferSuccess() {
      this.getList(1)
    },
    loadMore() {
      this.getList()
    },
    getList(page = 0, keyword = '') {
      this.clearCountdownData()
      if (page > 0) {
        this.videoList = []
        this.page = page
        this.hasMore = true
      }
      if (this.loading) return

      this.loading = true
      this.$api.videoAdv
        .getMyAdvList({
          page: this.page,
          size: this.size,
          keyword: keyword
        })
        .then((res) => {
          this.loading = false
          this.page++
          if (res.data.length === 0) {
            this.hasMore = false
            return
          }
          res.data.forEach((item) => {
            this.videoList.push(item)
          })
          this.hasMore = res.data.length >= this.size
          this.$nextTick(() => {
            this.startCountdown()
          })
        })
        .catch(() => {
          this.loading = false
        })
    },
    formatDate(date) {
      if (!date) return '--'
      const d = new Date(date)
      return d.toLocaleDateString('zh-CN') + ' ' + d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
/* ============================================================
   深灰主题 - 字号优化版
   主背景: #14181c | 卡片: #1a1f26 | 边框: #2a3038
   ============================================================ */

.adv-my-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 4px 30px;
  color: #e8edf2;
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #1a1f26;
  border-radius: 12px;
  border: 2px dashed #2a3038;
}
.empty-icon {
  font-size: 56px;
  margin-bottom: 16px;
}
.empty-state h3 {
  font-size: 22px;
  color: #e8edf2;
  margin: 0 0 8px 0;
}
.empty-desc {
  color: #6a7a8a;
  font-size: 15px;
  margin: 0;
}

/* ============================================================
   卡片
   ============================================================ */
.adv-my-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.adv-my-card {
  background: #1a1f26;
  border-radius: 14px;
  border: 1px solid #2a3038;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.adv-my-card:hover {
  border-color: #ff9800;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

/* ============================================================
   上半部分
   ============================================================ */
.adv-card-top {
  display: flex;
}

/* ===== 封面 ===== */
.adv-my-cover {
  margin-left: 20px;
  margin-top: 20px;
  flex: 0 0 250px;
  position: relative;
  overflow: hidden;
  background: #0f1419;
  min-height: 160px;
  border-radius: 10px;
}
.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ===== 封面状态标签 ===== */
.cover-status {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}
.cover-status .status-badge {
  padding: 5px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
.cover-status .badge-active {
  background: rgba(255, 152, 0, 0.85);
  color: #fff;
}
.cover-status .badge-settlement {
  background: rgba(33, 150, 243, 0.85);
  color: #fff;
}
.cover-status .badge-success {
  background: rgba(76, 175, 80, 0.85);
  color: #fff;
}

/* ===== 中间信息 ===== */
.adv-my-info {
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.adv-title {
  font-size: 20px;
  font-weight: 700;
  color: #e8edf2;
  margin: 0;
}
.adv-code {
  font-size: 13px;
  color: #6a7a8a;
  background: #0f1419;
  padding: 3px 14px;
  border-radius: 12px;
}

.adv-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 16px;
  background: #0f1419;
  border-radius: 8px;
  border: 1px solid #2a3038;
}
.stat-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.stat-label {
  font-size: 12px;
  color: #d5d5d5;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #e8edf2;
}
.stat-amount {
  color: #ffb300;
}
.stat-profit {
  color: #26a69a;
}
.stat-time {
  font-size: 14px;
  font-weight: 500;
  color: #6a7a8a;
}

.adv-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.btn-pay {
  padding: 8px 28px;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #e50914, #b20710);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(229, 9, 20, 0.25);
}
.btn-pay:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(229, 9, 20, 0.35);
}

/* ===== 右侧：播放来源 ===== */
.adv-source-right {
  padding: 16px 18px 14px;
  background: #0f1419;
  border-left: 1px solid #2a3038;
  display: flex;
  flex-direction: column;
  border-radius: 0 12px 12px 0;
  min-width: 220px;
}

.source-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #2a3038;
}
.source-title {
  font-size: 15px;
  font-weight: 700;
  color: #e8edf2;
}
.source-platform {
  font-size: 12px;
  color: #6a7a8a;
  background: #1a1f26;
  padding: 3px 12px;
  border-radius: 10px;
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}
.source-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.source-info {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 85px;
}
.source-name {
  font-size: 13px;
  color: #b0bcc8;
  display: flex;
  align-items: center;
  gap: 5px;
}
.source-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}
.dot-facebook {
  background: #1877f2;
}
.dot-instagram {
  background: #e4405f;
}
.dot-tiktok {
  background: #f44336;
}
.dot-youtube {
  background: #4285f4;
}
.dot-other {
  background: #6c757d;
}

.source-percent {
  font-size: 13px;
  font-weight: 600;
  color: #e8edf2;
  min-width: 38px;
  text-align: right;
}
.source-bar {
  flex: 1;
  height: 4px;
  background: #2a3038;
  border-radius: 4px;
  overflow: hidden;
}
.source-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}
.source-count {
  font-size: 12px;
  color: #6a7a8a;
  min-width: 65px;
  text-align: right;
}

.source-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #2a3038;
}
.total-label {
  font-size: 14px;
  color: #6a7a8a;
  font-weight: 500;
}
.total-value {
  font-size: 17px;
  font-weight: 700;
  color: #ffb300;
}

/* ============================================================
   下半部分：时段支付
   ============================================================ */
.adv-card-bottom {
  padding: 18px 22px 22px;
  border-top: 1px solid #2a3038;
}

.period-header {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.period-title {
  font-size: 17px;
  font-weight: 700;
  color: #e8edf2;
}
.period-desc {
  font-size: 14px;
  color: #6a7a8a;
}

.period-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.period-card {
  background: #0f1419;
  border-radius: 10px;
  border: 1px solid #2a3038;
  padding: 14px 16px 12px;
  transition: border-color 0.3s ease;
}
.period-card:hover {
  border-color: #ff9800;
}

.period-time {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #2a3038;
  margin-bottom: 10px;
}
.period-label {
  font-size: 14px;
  color: #6a7a8a;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.period-range {
  font-size: 15px;
  font-weight: 700;
  color: #e8edf2;
  margin: 2px 0;
}
.period-name {
  font-size: 15px;
  color: #6a7a8a;
}

.period-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}
.period-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
}
.period-row-label {
  color: #6a7a8a;
}
.period-row-value {
  color: #e8edf2;
  font-weight: 500;
}
.period-row-value.income-orange {
  color: #ffb300;
  font-weight: 700;
}

.period-pay-btn2 {
  width: 100%;
  padding: 7px 0;
  border: none;
  border-radius: 20px;
  background: #2f3742;
  color: #9affbc;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
}
.period-pay-btn3 {
  height: 33px;
  width: 100%;
  padding: 7px 0;
  border: none;
  border-radius: 20px;

  font-weight: 600;
  font-size: 14px;
  text-align: center;
}

.period-jxz {
  color: #ffc107;
}
.period-yrz {
  color: #56616a;
}

.period-pay-btn {
  width: 100%;
  padding: 7px 0;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #ff9800, #ff5722);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.period-pay-btn.disabled {
  background: #6a7a8a;
  cursor: not-allowed;
}

.period-pay-btn.expired {
  background: #f44336;
}

.period-pay-btn:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(255, 87, 34, 0.4);
}

/* ===== 已支付 / 已划转 ===== */
.period-paid-info {
  text-align: center;
  padding: 4px 0 6px;
}
.paid-amount {
  font-size: 13px;
  color: #6a7a8a;
}

.period-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #2a3038;
  font-size: 14px;
  font-weight: 600;
}
.period-status .status-icon {
  font-size: 16px;
}
.period-status .status-text {
  font-size: 14px;
}

/* 已划转 - 绿色 */
.status-transferred .status-text {
  color: #4caf50;
}

/* 已超时 - 红色 */
.status-expired .status-text {
  color: #f44336;
}

/* ===== 倒计时 ===== */
.period-countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #2a3038;
}
.countdown-icon {
  font-size: 14px;
}
.countdown-time {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 16px;
  color: #ffb300;
  letter-spacing: 1px;
}

/* ============================================================
   今日支付汇总
   ============================================================ */
.summary-box {
  margin-bottom: 16px;
  padding: 14px 18px;
  background: #0f1419;
  border-radius: 10px;
  border: 1px solid #2a3038;
}
.summary-title {
  font-size: 16px;
  font-weight: 700;
  color: #e8edf2;
  margin-bottom: 10px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}
.summary-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.summary-label {
  font-size: 12px;
  color: #6a7a8a;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.summary-value {
  font-size: 17px;
  font-weight: 700;
  color: #e8edf2;
}
.summary-value.income-orange {
  color: #ffb300;
}

/* ============================================================
   加载更多
   ============================================================ */
.load-more {
  text-align: center;
  padding: 30px 0 10px;
}
.load-btn {
  padding: 10px 36px;
  border: 1px solid #2a3038;
  border-radius: 30px;
  background: #1a1f26;
  color: #8a949f;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.load-btn:hover:not(:disabled) {
  border-color: #e50914;
  color: #e8edf2;
}
.load-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.loader {
  width: 18px;
  height: 18px;
  border: 2px solid #2a3038;
  border-top-color: #e50914;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.no-more {
  color: #6a7a8a;
  font-size: 15px;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 1024px) {
  .period-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 820px) {
  .adv-card-top {
    flex-wrap: wrap;
  }
  .adv-my-cover {
    flex: 0 0 100%;
    height: 130px;
    margin: 12px 12px 0;
  }
  .adv-my-info {
    flex: 1 1 60%;
    min-width: 200px;
  }
  .adv-source-right {
    flex: 1 1 30%;
    min-width: 180px;
    border-left: none;
    border-top: 1px solid #2a3038;
    border-radius: 0;
  }
}

@media (max-width: 768px) {
  .adv-card-top {
    flex-direction: column;
  }
  .adv-my-cover {
    height: 140px;
    width: calc(100% - 24px);
    margin: 12px 12px 0;
  }
  .adv-my-info {
    padding: 14px 16px;
  }
  .adv-source-right {
    border-left: none;
    border-top: 1px solid #2a3038;
    flex: 1;
    border-radius: 0;
  }
  .adv-stats {
    gap: 6px 16px;
    padding: 8px 12px;
  }
  .stat-item {
    flex: 1;
    min-width: 60px;
  }
  .stat-value {
    font-size: 14px;
  }
  .adv-footer {
    justify-content: stretch;
  }
  .btn-pay {
    width: 100%;
    text-align: center;
  }
  .adv-card-bottom {
    padding: 14px 16px 16px;
  }
  .period-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .period-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .period-card {
    padding: 10px 12px 10px;
  }
  .period-range {
    font-size: 13px;
  }
  .period-row {
    font-size: 13px;
  }
  .period-row-value {
    font-size: 13px;
  }
  .period-pay-btn {
    font-size: 13px;
    padding: 5px 0;
  }
  .summary-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .summary-value {
    font-size: 15px;
  }
  .adv-source-right {
    flex: 1;
    padding: 12px 14px;
  }
  .source-item {
    flex-wrap: wrap;
    gap: 4px;
  }
  .source-info {
    min-width: 65px;
  }
  .source-count {
    min-width: 45px;
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .adv-my-container {
    padding: 0 2px 20px;
  }
  .adv-my-cover {
    height: 110px;
    margin: 8px 8px 0;
    width: calc(100% - 16px);
  }
  .adv-title {
    font-size: 17px;
  }
  .adv-stats {
    gap: 4px 10px;
    padding: 6px 10px;
  }
  .stat-value {
    font-size: 14px;
  }
  .adv-card-bottom {
    padding: 10px 12px 14px;
  }
  .period-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .period-range {
    font-size: 14px;
  }
  .period-name {
    font-size: 15px;
  }
  .period-row {
    font-size: 13px;
  }
  .period-row-value {
    font-size: 13px;
  }
  .period-pay-btn {
    font-size: 13px;
    padding: 6px 0;
  }
  .period-status .status-text {
    font-size: 13px;
  }
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }
  .summary-value {
    font-size: 15px;
  }
  .adv-source-right {
    padding: 10px 12px;
  }
  .source-title {
    font-size: 14px;
  }
  .source-name {
    font-size: 12px;
  }
  .source-percent {
    font-size: 12px;
    min-width: 32px;
  }
  .source-count {
    font-size: 11px;
    min-width: 40px;
  }
  .total-value {
    font-size: 15px;
  }
}
</style>
